import { IssueResolutionResponsibility, IssueStatus, SyncCoreDataDefinitionsEnumTranslator } from '@edgebox/sync-core-data-definitions';
import { ISyncCoreApi } from '@edgebox/sync-core-rest-client';
import { faAdd, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { IFilterDropdownOption } from '../Onboarding/ContentInventory/Components/FilterDropdownList';
import { MultiLevelDropdownPanel } from '../Onboarding/ContentInventory/Components/MultiLevelDropdownPanel';
import { BP_POPOVER_CLASSES } from './brandPresenceClasses';
import { useDismissOnOutsideClick } from './useDismissOnOutsideClick';
import { BRAND_PRESENCE_LIST_SEARCH_DEBOUNCE_MS } from './brandPresenceLists';
import { BRAND_PRESENCE_ISSUE_STATUS_ANY_TOKEN, BRAND_PRESENCE_ISSUE_STATUS_QUERY_TOKENS } from '../brandPresencePaths';
import { NAME_LOADING_COPY, NameLoading } from './BrandPresenceSkeleton';

/** How many pages one search answer suggests. */
const PAGE_SEARCH_RESULTS = 20;

/** The filters of the issue list, as the tokens the address keeps for each of them. */
export interface IIssuesFilterValues {
  /** The status token the list reads, the one for any status included. */
  statusToken: string;
  issueTypeKey: string;
  responsibilityToken: string;
  pageKey: string;
  siteId: string;
}

type IssueFilterTargetId = 'status' | 'issueType' | 'responsibility' | 'page' | 'site';

/**
 * What a picked value is called where its name cannot be read: a page with no title or address, a site
 * this session cannot read, a type the project's list does not name. Its key or id is an identifier and
 * is never shown. The list says the same of a health check whose name cannot be read.
 */
export const NAME_NOT_AVAILABLE_COPY = 'Name not available';
/** Said in place of the page suggestions when the page search failed, above the button that asks again. */
const PAGE_SEARCH_FAILED_COPY = 'The pages could not be loaded.';

/** Which of the picked values' names are still being read. */
export interface IIssuesFilterNamesLoading {
  issueType: boolean;
  page: boolean;
  site: boolean;
}

const STATUS_TARGET: IFilterDropdownOption = { id: 'status', label: 'Status' };
const ISSUE_TYPE_TARGET: IFilterDropdownOption = { id: 'issueType', label: 'Issue type' };
const RESPONSIBILITY_TARGET: IFilterDropdownOption = { id: 'responsibility', label: 'Who fixes it' };
const PAGE_TARGET: IFilterDropdownOption = { id: 'page', label: 'Page' };
const SITE_TARGET: IFilterDropdownOption = { id: 'site', label: 'Site' };

function statusLabelOfToken(token: string): string | undefined {
  const status = (Object.values(IssueStatus) as IssueStatus[]).find((value) => BRAND_PRESENCE_ISSUE_STATUS_QUERY_TOKENS[value] === token);
  return status ? SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('IssueStatus', status) : undefined;
}

function responsibilityLabelOf(value: string): string | undefined {
  const known = (Object.values(IssueResolutionResponsibility) as string[]).includes(value);
  return known
    ? SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('IssueResolutionResponsibility', value as IssueResolutionResponsibility)
    : undefined;
}

/**
 * The one page option a search answer turns into: the page's whole title, with its URL beneath, which
 * is what tells two pages of the same title apart. A page known by neither is not offered, because its
 * key is an identifier, not words.
 */
function pageOption(item: { key?: string; name?: string; url?: string }): IFilterDropdownOption | undefined {
  const key = String(item.key || '').trim();
  const url = String(item.url || '').trim() || undefined;
  const title = String(item.name || '').trim() || url;
  if (!key || !title) {
    return undefined;
  }
  return { id: key, label: title, description: url };
}

/**
 * The issue list's filters as the content inventory's Add Filter chips: one chip per active filter,
 * and one menu that first names the filter, then its values.
 *
 * The Issue type values are every type of the project, not just those on the current page, so the
 * choice never shrinks to the type already picked. The Page filter is a searchable picker: it asks
 * for pages as the reader types and suggests each page by its title with its URL, in place of a
 * free-text URL field.
 */
export function IssuesAddFilterDropdown({
  api,
  values,
  onChange,
  issueTypeOptions,
  siteOptions,
  offerSiteFilter,
  pageLabel,
  namesLoading,
}: {
  api?: ISyncCoreApi;
  values: IIssuesFilterValues;
  onChange: (values: IIssuesFilterValues) => void;
  /** The issue types the list can name, as `[key, name]`: every named type of the project and of the loaded rows. */
  issueTypeOptions: [string, string][];
  /** The project's sites, as `[id, name]`. */
  siteOptions: [string, string][];
  offerSiteFilter: boolean;
  /** What the Page chip calls the picked page, where the screen has resolved its title. */
  pageLabel?: string;
  /** Which picked values' names are still being read, so their chips show them loading. */
  namesLoading: IIssuesFilterNamesLoading;
}) {
  const [open, setOpen] = React.useState(false);
  const editorRef = React.useRef<HTMLDivElement>(null);
  const openerRef = React.useRef<HTMLElement | null>(null);
  const [draft, setDraft] = React.useState<IIssuesFilterValues>(values);
  const [selectedTargetId, setSelectedTargetId] = React.useState<IssueFilterTargetId | undefined>(undefined);
  const [targetSearch, setTargetSearch] = React.useState('');
  const [valueSearch, setValueSearch] = React.useState('');
  const [pageOptions, setPageOptions] = React.useState<IFilterDropdownOption[]>([]);
  const [loadingPages, setLoadingPages] = React.useState(false);
  /** Whether the last page search failed, which is not the same as one that found no page. */
  const [pageSearchFailed, setPageSearchFailed] = React.useState(false);
  /**
   * How many times the reader asked a failed page search to try again; each asks anew. The search is not a
   * statistics request, so it counts its own attempts, as the list's own read does, rather than through useRetry,
   * which also resumes the statistics queue.
   */
  const [pageSearchAttempt, setPageSearchAttempt] = React.useState(0);
  const retryPageSearch = React.useCallback(() => setPageSearchAttempt((current) => current + 1), []);

  const targets = React.useMemo(() => {
    const offered = [STATUS_TARGET, ISSUE_TYPE_TARGET, RESPONSIBILITY_TARGET, PAGE_TARGET];
    return offerSiteFilter ? [...offered, SITE_TARGET] : offered;
  }, [offerSiteFilter]);

  // The page suggestions follow the typed search, one ask per pause in the typing, and ask again when
  // the reader asks a failed search to try again. Only the Page filter asks; every other filter's
  // values are already on hand.
  React.useEffect(() => {
    if (!open || selectedTargetId !== 'page' || !api) {
      return;
    }

    let cancelled = false;
    setLoadingPages(true);
    setPageSearchFailed(false);
    const timeout = setTimeout(() => {
      void (async () => {
        try {
          const found = await api.governance.contentItems.list(
            { page: 0, itemsPerPage: PAGE_SEARCH_RESULTS },
            { search: valueSearch.trim() || undefined }
          );
          if (cancelled) {
            return;
          }
          setPageOptions((found.items || []).map((item) => pageOption(item)).filter((option): option is IFilterDropdownOption => !!option));
        } catch {
          // A search that failed found nothing out, so it says so and offers to ask again, rather than
          // reading as a search no page matches.
          if (cancelled) {
            return;
          }
          setPageOptions([]);
          setPageSearchFailed(true);
        } finally {
          if (!cancelled) {
            setLoadingPages(false);
          }
        }
      })();
    }, BRAND_PRESENCE_LIST_SEARCH_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [api, open, selectedTargetId, valueSearch, pageSearchAttempt]);

  const statusValueOptions = React.useMemo<IFilterDropdownOption[]>(
    () => [
      ...(Object.values(IssueStatus) as IssueStatus[]).map((status) => ({
        id: BRAND_PRESENCE_ISSUE_STATUS_QUERY_TOKENS[status],
        label: SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('IssueStatus', status),
      })),
      { id: BRAND_PRESENCE_ISSUE_STATUS_ANY_TOKEN, label: 'Any' },
    ],
    []
  );

  const issueTypeValueOptions = React.useMemo<IFilterDropdownOption[]>(
    () => issueTypeOptions.map(([key, label]) => ({ id: key, label })),
    [issueTypeOptions]
  );

  const responsibilityValueOptions = React.useMemo<IFilterDropdownOption[]>(
    () =>
      (Object.values(IssueResolutionResponsibility) as IssueResolutionResponsibility[]).map((value) => ({
        id: value,
        label: SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('IssueResolutionResponsibility', value),
      })),
    []
  );

  const siteValueOptions = React.useMemo<IFilterDropdownOption[]>(() => siteOptions.map(([id, label]) => ({ id, label })), [siteOptions]);

  const valueOptionsByTarget: Record<IssueFilterTargetId, IFilterDropdownOption[]> = {
    status: statusValueOptions,
    issueType: issueTypeValueOptions,
    responsibility: responsibilityValueOptions,
    page: pageOptions,
    site: siteValueOptions,
  };

  const selectedValueIdByTarget: Record<IssueFilterTargetId, string | undefined> = {
    status: draft.statusToken,
    issueType: draft.issueTypeKey || undefined,
    responsibility: draft.responsibilityToken || undefined,
    page: draft.pageKey || undefined,
    site: draft.siteId || undefined,
  };

  const filteredValues = React.useMemo(() => {
    if (!selectedTargetId) {
      return [];
    }
    const options = valueOptionsByTarget[selectedTargetId];
    if (selectedTargetId === 'page') {
      // The search already narrowed the answer on the server.
      return options;
    }
    const needle = valueSearch.trim().toLowerCase();
    return needle ? options.filter((option) => option.label.toLowerCase().includes(needle)) : options;
  }, [selectedTargetId, valueOptionsByTarget, valueSearch]);

  const selectedValueOptions = React.useMemo(() => {
    if (!selectedTargetId) {
      return [];
    }
    const selectedId = selectedValueIdByTarget[selectedTargetId];
    if (!selectedId) {
      return [];
    }
    const known = valueOptionsByTarget[selectedTargetId].find((option) => option.id === selectedId);
    if (known) {
      return [known];
    }
    // A value is named by its name; its key or id is an identifier and is never shown.
    const stillLoading = selectedTargetId !== 'status' && selectedTargetId !== 'responsibility' && namesLoading[selectedTargetId];
    const unnamed = stillLoading ? `${NAME_LOADING_COPY}…` : NAME_NOT_AVAILABLE_COPY;
    return [{ id: selectedId, label: (selectedTargetId === 'page' ? pageLabel : undefined) || unnamed }];
  }, [namesLoading, pageLabel, selectedTargetId, selectedValueIdByTarget, valueOptionsByTarget]);

  const applyDraftValue = (targetId: IssueFilterTargetId, option: IFilterDropdownOption) => {
    setDraft((current) => {
      switch (targetId) {
        case 'status':
          return { ...current, statusToken: option.id };
        case 'issueType':
          return { ...current, issueTypeKey: current.issueTypeKey === option.id ? '' : option.id };
        case 'responsibility':
          return { ...current, responsibilityToken: current.responsibilityToken === option.id ? '' : option.id };
        case 'page':
          return { ...current, pageKey: current.pageKey === option.id ? '' : option.id };
        case 'site':
          return { ...current, siteId: current.siteId === option.id ? '' : option.id };
      }
    });
  };

  const chips = React.useMemo(() => {
    // A value whose name is still being read shows it loading; one whose name cannot be read says so. Its
    // key is never shown.
    const entries: { targetId: IssueFilterTargetId; targetLabel: string; valueLabel: string | undefined; onRemove: () => void }[] = [];

    if (values.statusToken !== BRAND_PRESENCE_ISSUE_STATUS_ANY_TOKEN) {
      entries.push({
        targetId: 'status',
        targetLabel: STATUS_TARGET.label,
        valueLabel: statusLabelOfToken(values.statusToken) ?? NAME_NOT_AVAILABLE_COPY,
        onRemove: () => onChange({ ...values, statusToken: BRAND_PRESENCE_ISSUE_STATUS_ANY_TOKEN }),
      });
    }
    if (values.issueTypeKey) {
      const known = issueTypeOptions.find(([key]) => key === values.issueTypeKey);
      entries.push({
        targetId: 'issueType',
        targetLabel: ISSUE_TYPE_TARGET.label,
        valueLabel: known?.[1] ?? (namesLoading.issueType ? undefined : NAME_NOT_AVAILABLE_COPY),
        onRemove: () => onChange({ ...values, issueTypeKey: '' }),
      });
    }
    if (values.responsibilityToken) {
      entries.push({
        targetId: 'responsibility',
        targetLabel: RESPONSIBILITY_TARGET.label,
        valueLabel: responsibilityLabelOf(values.responsibilityToken) ?? NAME_NOT_AVAILABLE_COPY,
        onRemove: () => onChange({ ...values, responsibilityToken: '' }),
      });
    }
    if (values.pageKey) {
      entries.push({
        targetId: 'page',
        targetLabel: PAGE_TARGET.label,
        valueLabel: pageLabel || (namesLoading.page ? undefined : NAME_NOT_AVAILABLE_COPY),
        onRemove: () => onChange({ ...values, pageKey: '' }),
      });
    }
    if (values.siteId) {
      const known = siteOptions.find(([id]) => id === values.siteId);
      entries.push({
        targetId: 'site',
        targetLabel: SITE_TARGET.label,
        valueLabel: known?.[1] ?? (namesLoading.site ? undefined : NAME_NOT_AVAILABLE_COPY),
        onRemove: () => onChange({ ...values, siteId: '' }),
      });
    }
    return entries;
  }, [issueTypeOptions, namesLoading, onChange, pageLabel, siteOptions, values]);

  const openEditor = (targetId?: IssueFilterTargetId) => {
    setDraft(values);
    setTargetSearch('');
    setValueSearch('');
    setPageOptions([]);
    setSelectedTargetId(targetId);
    setOpen(true);
  };

  const closeEditor = React.useCallback(() => {
    setOpen(false);
    setSelectedTargetId(undefined);
    setTargetSearch('');
    setValueSearch('');
  }, []);

  useDismissOnOutsideClick({ open, containerRef: editorRef, triggerRef: openerRef, onDismiss: closeEditor });

  const filteredTargets = React.useMemo(() => {
    const needle = targetSearch.trim().toLowerCase();
    return needle ? targets.filter((target) => target.label.toLowerCase().includes(needle)) : targets;
  }, [targetSearch, targets]);

  const selectedTarget = targets.find((target) => target.id === selectedTargetId);

  return (
    <div className="relative flex items-center gap-2 flex-wrap">
      {chips.map((chip) => (
        <button
          key={chip.targetId}
          type="button"
          className="inline-flex h-8 items-center gap-2 rounded-full bg-primary/[0.07] pl-3 pr-2 text-xs text-slate-700 ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/[0.12] motion-safe:animate-bp-fade-in"
          onClick={(event) => {
            openerRef.current = event.currentTarget;
            openEditor(chip.targetId);
          }}
        >
          <span className="truncate max-w-[220px]">
            <span className="font-semibold">{chip.targetLabel}:</span> {chip.valueLabel ?? <NameLoading />}
          </span>
          <span
            role="button"
            tabIndex={0}
            aria-label="Remove filter"
            className="inline-flex h-5 w-5 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-white hover:text-slate-700"
            onClick={(event) => {
              event.stopPropagation();
              chip.onRemove();
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                event.stopPropagation();
                chip.onRemove();
              }
            }}
            title="Remove filter"
          >
            <FontAwesomeIcon icon={faXmark} className="text-[10px]" />
          </span>
        </button>
      ))}

      <div className="relative" ref={editorRef}>
        <button
          type="button"
          className={`inline-flex h-8 items-center gap-1.5 rounded-full px-3 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
            open ? 'bg-primary/10 text-primary' : 'text-primary hover:bg-primary/[0.07]'
          }`}
          aria-expanded={open}
          onClick={(event) => {
            openerRef.current = event.currentTarget;
            if (open) {
              closeEditor();
              return;
            }
            openEditor(undefined);
          }}
        >
          <FontAwesomeIcon icon={faAdd} className="text-xs" /> Add Filter
        </button>

        {open ? (
          <div className={`absolute left-0 top-full z-20 mt-2 w-[360px] ${BP_POPOVER_CLASSES}`}>
            <MultiLevelDropdownPanel
              selectedTarget={selectedTarget ? { id: selectedTarget.id, label: selectedTarget.label } : undefined}
              targetSearch={targetSearch}
              onTargetSearchChange={setTargetSearch}
              targetSearchPlaceholder="Search filters"
              selectedTargetOptions={[]}
              filteredTargetOptions={filteredTargets}
              onSelectTarget={(option) => {
                setSelectedTargetId(option.id as IssueFilterTargetId);
                setValueSearch('');
                setPageOptions([]);
              }}
              valueSearch={valueSearch}
              onValueSearchChange={setValueSearch}
              valueSearchPlaceholder={selectedTargetId === 'page' ? 'Search pages' : 'Search values'}
              selectedValueOptions={selectedValueOptions}
              filteredValueOptions={filteredValues}
              loadingValues={selectedTargetId === 'page' ? loadingPages : false}
              valueSelectionMultiple={false}
              onToggleValue={(option) => {
                if (selectedTargetId) {
                  applyDraftValue(selectedTargetId, option);
                }
              }}
              hideSelectedValuesFromFiltered={false}
              changeTargetLabel="Change filter"
              onBackToTargets={() => {
                setSelectedTargetId(undefined);
                setValueSearch('');
              }}
              onCancel={closeEditor}
              onApply={() => {
                onChange(draft);
                closeEditor();
              }}
              emptyValuesMessage={selectedTargetId === 'page' && !loadingPages ? 'No page matches this search.' : undefined}
              valuesLoadFailed={
                selectedTargetId === 'page' && pageSearchFailed ? { message: PAGE_SEARCH_FAILED_COPY, onRetry: retryPageSearch } : undefined
              }
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
